import type * as React from "react";

import { cx } from "../../lib/cx";
import { iniciales } from "../../lib/iniciales";
import { Icon, type IconName } from "./Icon";
import s from "./Avatar.module.css";

export type AvatarSize = "xs" | "sm" | "md" | "lg";

export interface AvatarProps extends React.ComponentPropsWithoutRef<"span"> {
  /** De acá salen las iniciales y, si no hay `alt`, el texto alternativo. */
  nombre: string;
  /** La foto. Sin ella van las iniciales en violeta sobre accent-100. */
  src?: string | null;
  /** xs 24 (listas densas), sm 28 (barra lateral), md 40 (tablas, fichas), lg 56 (perfil). */
  size?: AvatarSize;
  /** `soft` (default): iniciales violeta sobre accent-100. `solid`: blancas sobre violeta, para la cuenta de la barra
   *  lateral. `muted`: grises sobre surface, una persona desactivada. `dashed`: blanco con borde punteado, alguien que
   *  todavía no existe (una invitación pendiente); suele ir con `icon="mail"`. */
  tone?: "soft" | "solid" | "muted" | "dashed";
  /** Un icono en lugar de las iniciales: el sobre de una invitación que todavía no aceptaron. */
  icon?: IconName;
  /** El nombre ya está escrito al lado: el avatar no repite nada. */
  decorative?: boolean;
  alt?: string;
}

/** La foto de una persona o, a falta de ella, sus iniciales. Una sola implementación para toda la app. */
export function Avatar({ nombre, src, size = "md", tone = "soft", icon, decorative = false, alt, className, ...rest }: AvatarProps) {
  const texto = decorative ? "" : (alt ?? nombre);
  return (
    <span
      className={cx(s.avatar, className)}
      data-size={size}
      data-tone={tone}
      aria-hidden={decorative || undefined}
      role={src || decorative ? undefined : "img"}
      aria-label={src || decorative ? undefined : texto}
      {...rest}
    >
      {src ? (
        // eslint-disable-next-line @next/next/no-img-element -- foto del bucket público, sin optimizador
        <img src={src} alt={texto} className={s.foto} />
      ) : icon ? (
        <Icon name={icon} size={size === "lg" ? 20 : size === "md" ? 14 : 12} />
      ) : (
        <span className={s.iniciales} aria-hidden="true">
          {iniciales(nombre)}
        </span>
      )}
    </span>
  );
}
